import type { CustomSeriesOption, CustomSeriesRenderItemAPI, CustomSeriesRenderItemParams } from 'echarts';

import type { TimeRangeLane } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import { getTimeRangeLaneColor } from '@/shared/charts/time-series-chart/theme/getTimeRangeLaneColor';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { rangeBarVisuals } from '../config/chartVisuals';
import { getRangeTrackSeriesId } from '../config/componentIds';
import { getLaneHeight } from './getLaneHeight';

/** Data dimensions of one bar item: `[laneIndex, startTimestamp, endTimestamp]`. */
const LANE_DIMENSION = 0;
const START_DIMENSION = 1;
const END_DIMENSION = 2;

type RangeBarItem = [number, number, number];

type BuildRangeBarsOptionParams = {
  lane: TimeRangeLane;
  laneIndex: number;
  xAxisIndex: number;
  yAxisIndex: number;
  theme: TimeSeriesChartTheme;
};

function buildLaneData(lane: TimeRangeLane, laneIndex: number): RangeBarItem[] {
  return lane.ranges.map(({ startTimestamp, endTimestamp }) => [
    laneIndex,
    Math.min(startTimestamp, endTimestamp),
    Math.max(startTimestamp, endTimestamp),
  ]);
}

function createRenderItem(theme: TimeSeriesChartTheme) {
  return function renderRangeBar(_params: CustomSeriesRenderItemParams, api: CustomSeriesRenderItemAPI) {
    const laneIndex = api.value(LANE_DIMENSION);
    const startPoint = api.coord([api.value(START_DIMENSION), laneIndex]);
    const endPoint = api.coord([api.value(END_DIMENSION), laneIndex]);

    const height = getLaneHeight(api) * rangeBarVisuals.barHeightRatio;
    const width = Math.max(endPoint[0] - startPoint[0], rangeBarVisuals.minimumBarWidth);

    return {
      type: 'rect' as const,
      shape: {
        x: startPoint[0],
        y: startPoint[1] - height / 2,
        width,
        height,
        r: theme.rangeTrack.barBorderRadius,
      },
      style: {
        fill: api.visual('color'),
      },
    };
  };
}

/** One clipped custom series per lane, drawing a bar for every range. */
export function buildRangeBarsOption({
  lane,
  laneIndex,
  xAxisIndex,
  yAxisIndex,
  theme,
}: BuildRangeBarsOptionParams): CustomSeriesOption {
  return {
    id: getRangeTrackSeriesId(lane.id),
    name: lane.label,
    type: 'custom',
    xAxisIndex,
    yAxisIndex,
    clip: true,
    silent: true,
    itemStyle: {
      color: getTimeRangeLaneColor(lane, theme),
    },
    renderItem: createRenderItem(theme),
    encode: {
      x: [START_DIMENSION, END_DIMENSION],
      y: LANE_DIMENSION,
    },
    data: buildLaneData(lane, laneIndex),
  };
}
